// customerAudit — admin-facing endpoints for the customer app
// backend. Distinct from the customer-app's public /v1/customer/*
// endpoints (which the mobile app calls) — these live under
// /v1/api/customer/* and are gated by streamauth admin+support.
//
// Powers:
//   · CustomerAuditPage — filtered login-attempt list + 24h summary
//   · Active-sessions card on SubscriberDetailPage — list + revoke

import type { ApiClient } from "./client.js";

/** Enum from customer_login_attempts.outcome. */
export type CustomerLoginOutcome =
  | "password_ok"
  | "password_fail"
  | "no_phone"
  | "locked"
  | "otp_sent"
  | "otp_ok"
  | "otp_fail"
  | "logged_out";

export interface CustomerLoginAttempt {
  id: number;
  username: string;
  ip: string;
  outcome: CustomerLoginOutcome;
  detail?: string;
  /** RFC3339 timestamp. */
  at: string;
}

export interface CustomerLoginAttemptsResponse {
  ok: boolean;
  attempts?: CustomerLoginAttempt[];
  /** Row count matching filter (for pagination). */
  total?: number;
  /** Outcome → count over the last 24h (fixed window; not affected by since_hours). */
  summary?: Partial<Record<CustomerLoginOutcome, number>>;
  error?: string;
}

export interface CustomerSession {
  id: number;
  username: string;
  device_label: string;
  ip_last: string;
  created_at: string;
  last_used_at: string;
  /** null when the session is still active. */
  revoked_at: string | null;
}

export interface CustomerSessionsResponse {
  ok: boolean;
  sessions?: CustomerSession[];
  error?: string;
}

export interface CustomerRevokeResponse {
  ok: boolean;
  revoked_count?: number;
  error?: string;
}

/** Query params for the audit list. */
export interface CustomerLoginAttemptsFilter {
  username?: string;
  outcome?: CustomerLoginOutcome;
  since_hours?: number;
  limit?: number;
  offset?: number;
}

export function makeCustomerAuditApi(client: ApiClient) {
  return {
    listAttempts(f: CustomerLoginAttemptsFilter = {}) {
      const q: Record<string, string> = {};
      if (f.username) q.username = f.username;
      if (f.outcome) q.outcome = f.outcome;
      if (f.since_hours != null) q.since_hours = String(f.since_hours);
      if (f.limit != null) q.limit = String(f.limit);
      if (f.offset != null) q.offset = String(f.offset);
      return client.get<CustomerLoginAttemptsResponse>(
        "/v1/api/customer/login_attempts",
        q,
      );
    },
    listSessions(username: string, includeRevoked = false) {
      return client.get<CustomerSessionsResponse>("/v1/api/customer/sessions", {
        username,
        ...(includeRevoked ? { include_revoked: "true" } : {}),
      });
    },
    /** Admin-side revoke (distinct from customer's self-logout). */
    revokeSession(sessionId: number, reason?: string) {
      return client.post<CustomerRevokeResponse>(
        "/v1/api/customer/sessions/revoke",
        { session_id: sessionId, reason: reason ?? "" },
      );
    },
  };
}

export type CustomerAuditApi = ReturnType<typeof makeCustomerAuditApi>;
